import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { tokens } from "../../../theme";
import { RootState } from "../../../store/Index";
import BarChartIcon from "@mui/icons-material/BarChart";
import LayersIcon from "@mui/icons-material/Layers";
import PublicIcon from "@mui/icons-material/Public";

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const FeaturesSection: React.FC = () => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);

  const features: Feature[] = [
    {
      icon: <BarChartIcon sx={{ fontSize: 28 }} />,
      title: "Executive Dashboards",
      description:
        "Real-time business intelligence and viewer metrics for every service, helping you measure brand impact.",
    },
    {
      icon: <LayersIcon sx={{ fontSize: 28 }} />,
      title: "Lead Generation",
      description:
        'Seamless "Request Policy Information" hooks integrated directly into the streaming platform.',
    },
    {
      icon: <PublicIcon sx={{ fontSize: 28 }} />,
      title: "Global Edge Network",
      description:
        "Zero-lag streaming infrastructure ensuring that family across the globe can participate in real-time.",
    },
  ];

  return (
    <Box
      id="features"
      component="section"
      sx={{
        py: { xs: 12, md: 15 },
        bgcolor: colors.primary[900],
        borderTop: `1px solid ${colors.primary[800]}`,
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: { xs: 6, md: 12 },
          }}
        >
          {features.map((feature, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              {/* Icon Box */}
              <Paper
                elevation={0}
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: `${colors.primary[400]}10`,
                  borderRadius: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                  color: colors.primary[400],
                }}
              >
                {feature.icon}
              </Paper>

              {/* Title */}
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  color: colors.primary[100],
                  mb: 1.5,
                }}
              >
                {feature.title}
              </Typography>

              {/* Description */}
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: colors.primary[300],
                  lineHeight: 1.6,
                }}
              >
                {feature.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default FeaturesSection;